<script>
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { storeToRefs } from "pinia";
import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { getErrorMessage } from "@/common/api/utils";
import VocabService from "@/common/api/vocab.service";
import {
  AnswerStateEnum,
  QuestionTypeEnum,
  QuizStateEnum
} from "@/common/composables/use-vocab-quiz";
import NotificationService from "@/common/notification.service";
import { ClassQuizStateEnum } from "@/common/types/vocab";
import { createQuestionText } from "@/common/vocabulary-trainer-helpers";
import ClassQuizAnswerPrompt from "@/components/vocabularyTrainer/ClassQuizAnswerPrompt.vue";
import ClassQuizBaseLayout from "@/components/vocabularyTrainer/ClassQuizBaseLayout.vue";
import ClassQuizQuestionPrompt from "@/components/vocabularyTrainer/ClassQuizQuestionPrompt.vue";
import MultipleChoiceGame from "@/components/vocabularyTrainer/games/MultipleChoiceGame.vue";
import TrueFalseGame from "@/components/vocabularyTrainer/games/TrueFalseGame.vue";
import QuizJumbotron from "@/components/vocabularyTrainer/QuizJumbotron.vue";
import { useAuthStore, useSettingsStore } from "@/stores";

import { useClassQuizComponent } from "./use-class-quiz-component";

const { commonProps, commonEmits } = useClassQuizComponent();

export default defineComponent({
  components: {
    ClassQuizAnswerPrompt,
    ClassQuizBaseLayout,
    ClassQuizQuestionPrompt,
    QuizJumbotron,
    TgButton,
    AbcHeading
  },
  props: commonProps,
  emits: commonEmits,
  setup(props, { emit }) {
    // Import globals
    const authStore = useAuthStore();
    const settingsStore = useSettingsStore();
    const { t } = useI18n();

    const { disableContinue, classQuiz } = toRefs(props);

    const { userIsTeacher } = storeToRefs(authStore);
    const quizState = ref(QuizStateEnum.Prompted);
    const currentQuestion = computed(() => classQuiz.value?.question);
    const renderKey = computed(() => currentQuestion.value?.id);

    // Countdown to question end
    const countdownInterval = ref(null);
    const durationMs = computed(() => classQuiz.value?.question?.maxDuration * 1000);
    const startTimestamp = computed(() => new Date(classQuiz.value?.question?.startedAt).getTime());
    const endTimestamp = computed(() =>
      new Date(startTimestamp.value + durationMs.value).getTime()
    );
    const countdown = ref(-1);

    const startCountdown = () => {
      if (!endTimestamp.value) {
        return;
      }

      countdown.value = Math.round((endTimestamp.value - new Date().getTime()) / 1000);

      countdownInterval.value = setInterval(() => {
        if (countdown.value < 0) {
          clearInterval(countdownInterval.value);
        }

        countdown.value = Math.max(countdown.value - 1, 0);
      }, 1000);
    };

    onMounted(() => {
      // Be sure countdown is started immediately.
      startCountdown();

      // Set quiz state from backends game-state.
      if (classQuiz.value.state === ClassQuizStateEnum.Prompting) {
        quizState.value = classQuiz.value.userAnswer
          ? QuizStateEnum.Answered
          : QuizStateEnum.Prompted;
      } else if (classQuiz.value.state === ClassQuizStateEnum.Answers) {
        quizState.value = QuizStateEnum.Answered;
      }
    });
    onUnmounted(() => clearInterval(countdownInterval.value));

    // Map game component
    const questionTypeComponentMap = {
      [QuestionTypeEnum.MultipleChoice]: MultipleChoiceGame,
      [QuestionTypeEnum.TrueFalse]: TrueFalseGame
    };
    const questionTypeComponent = computed(
      () => questionTypeComponentMap[currentQuestion.value?.type] ?? null
    );

    // Quiz specific
    const correctAnswer = ref(null);
    const selectedAnswer = ref(null);
    const totalAnswerCount = computed(() => classQuiz.value?.totalAnswerCount ?? 0);
    const answerState = computed(() => {
      if (!correctAnswer.value && !selectedAnswer.value) {
        return null;
      }

      if (!correctAnswer.value && selectedAnswer.value) {
        return {
          correctAnswer: null,
          userAnswer: selectedAnswer.value,
          state: AnswerStateEnum.Pending
        };
      }

      if (selectedAnswer.value === correctAnswer.value) {
        return {
          correctAnswer: correctAnswer.value,
          userAnswer: selectedAnswer.value,
          state: AnswerStateEnum.Correct
        };
      }

      return {
        correctAnswer: correctAnswer.value,
        userAnswer: selectedAnswer.value,
        state: AnswerStateEnum.Incorrect
      };
    });

    const validateAnswer = () => {
      if (userIsTeacher.value) {
        quizState.value = QuizStateEnum.Prompted;
        return AnswerStateEnum.Initial;
      }

      quizState.value = QuizStateEnum.Answered;
      return AnswerStateEnum.Pending;
    };
    const onAnswer = async (answer) => {
      if (userIsTeacher.value) {
        return;
      }

      selectedAnswer.value = answer;

      try {
        await VocabService.createClassQuizAnswer(
          props.classQuiz.code,
          props.classQuiz.question.id,
          answer
        );
      } catch (error) {
        if (getErrorMessage(error) === "class_quiz_no_question") {
          NotificationService.error(t("api.class_quiz_no_question"));
        } else if (getErrorMessage(error) === "class_quiz_question_ended") {
          NotificationService.error(t("api.class_quiz_question_ended"));
        }
      }
    };
    const onContinue = () => {
      correctAnswer.value = null;
      selectedAnswer.value = null;

      emit("continue");

      quizState.value = QuizStateEnum.Prompted;
    };

    const showQuestion = computed(
      () => questionTypeComponent.value && Array.isArray(currentQuestion.value?.options)
    );
    const question = computed(() =>
      createQuestionText(
        currentQuestion.value?.type,
        currentQuestion.value,
        settingsStore.activeLanguageGroup
      )
    );
    const showContinue = computed(
      () =>
        (countdown.value <= 0 || quizState.value === QuizStateEnum.Answered) && userIsTeacher.value
    );

    // Jumbotron stuff
    const answeredCorrectly = computed(() => {
      if (userIsTeacher.value) {
        return undefined;
      }

      if (answerState.value === null || answerState.value.state === AnswerStateEnum.Pending) {
        return undefined;
      }

      return answerState.value.state === AnswerStateEnum.Correct;
    });
    const jumbotronColor = computed(() => {
      if (answeredCorrectly.value === undefined) {
        return "secondary";
      }

      return answeredCorrectly.value === true ? "primary" : "red";
    });
    const displayCountdown = computed(() => {
      if (correctAnswer.value !== null) {
        return 0;
      }

      return Math.max(0, countdown.value);
    });

    // If the quiz answer changes, update the correct answer and quiz state.
    watchEffect(() => {
      correctAnswer.value = classQuiz.value?.answer;
      quizState.value = correctAnswer.value ? QuizStateEnum.Answered : quizState.value;
    });

    watchEffect(() => {
      selectedAnswer.value = classQuiz.value?.userAnswer;
    });

    return {
      // Global framework
      t,

      // Render key
      showQuestion,

      // Jumbotron/prompt stuff
      jumbotronColor,
      answeredCorrectly,
      displayCountdown,

      // Question/quiz
      question,
      questionTypeComponent,
      renderKey,
      currentQuestion,
      quizState,
      answerState,
      totalAnswerCount,
      validateAnswer,
      onAnswer,

      // Footer action
      showContinue,
      disableContinue,
      userIsTeacher,
      onContinue
    };
  }
});
</script>

<template>
  <ClassQuizBaseLayout v-if="showQuestion">
    <template #jumbotron>
      <QuizJumbotron :color="jumbotronColor">
        <div class="container">
          <ClassQuizQuestionPrompt
            v-if="answeredCorrectly === undefined"
            :countdown="displayCountdown"
          />
          <ClassQuizAnswerPrompt v-else :correct="answeredCorrectly" />
        </div>
      </QuizJumbotron>
    </template>

    <template #question>
      <div class="container">
        <AbcHeading
          id="class-quiz-heading"
          tag="h1"
          size="xl"
          color="black"
          class="text-center class-quiz__heading"
          lang="en"
        >
          {{ question }}
        </AbcHeading>
      </div>
    </template>

    <div class="container">
      <Component
        :is="questionTypeComponent"
        :key="renderKey"
        class="class-quiz"
        prompt-id="class-quiz-heading"
        :quiz-state="quizState"
        :questions="[currentQuestion]"
        :user-answers="[]"
        :answer-validator="validateAnswer"
        :answer-state="answerState"
        :explicit-answer-state="userIsTeacher"
        :total-answer-count="totalAnswerCount"
        @answer="onAnswer($event)"
      />
    </div>

    <template v-if="userIsTeacher" #footer>
      <div class="container text-center">
        <TgButton
          class="class-quiz-question-footer-btn"
          size="sm"
          size-md="md"
          :aria-hidden="!showContinue"
          :tabindex="showContinue ? 0 : -1"
          :class="{ 'class-quiz-question-footer-btn--visually-hidden': !showContinue }"
          :disabled="disableContinue"
          @click="onContinue()"
        >
          {{ t("vocab.classQuiz.question.continue") }}
        </TgButton>
      </div>
    </template>
  </ClassQuizBaseLayout>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.class-quiz {
  margin-top: 5vmin;

  &__heading {
    #media-breakpoint-up(xl, {
      font-size: 250%;
      line-height: 1.15em;
    });
  }
}

.class-quiz-question-footer-btn {
  transition: opacity 150ms ease-in-out;

  &--visually-hidden {
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
  }
}
</style>
